<template>
  <div class="order-rate-shop-tab" :class="tab.key">
    <SpotMarketTable
      :id="`spot-markets-for-shipment-spec-${shipmentSpecId}`"
      :spot-markets="spotMarkets"
      :selectable="selectable"
      @rate-selected="onRateSelected"
      @spot-market-cancelled="onSpotMarketCancelled"
      @spot-market-resent="onSpotMarketResent"
    >
      <template #empty>
        {{
          t('common.noParam', {
            param: t('carrierPortal.spotMarket.spotMarkets', 2),
          })
        }}
      </template>
    </SpotMarketTable>

    <RateSelectionReasonModal
      v-model:visible="lowestCostReasonModal.visible"
      :lowest-price-quote="lowestPriceQuote"
      :selected-quote="lowestCostReasonModal.selectedQuote"
      @confirm="onRateSelectionConfirmed"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, PropType, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import {
  useLowestCostReasonCode,
  useRateShop,
  useSpotMarketPolling,
} from '@/composables';
import {
  AcceptQuotePayload,
  RateQuote,
  RateSelectionReason,
  RateShop,
  SpotMarket,
} from '@/model';
import { useSpotMarketStore } from '@/stores/SpotMarket';

import { OrderRateShopTab } from '@/components/order/rateShop/OrderRateShopTabs.vue';
import RateSelectionReasonModal from '@/components/rateShop/RateSelectionReasonModal.vue';
import SpotMarketTable from '@/components/rateShop/SpotMarketTable.vue';

const props = defineProps({
  tab: {
    type: Object as PropType<OrderRateShopTab>,
    required: true,
  },
  shipmentSpecId: {
    type: Number,
    required: true,
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    required: true,
  },
  spotMarkets: {
    type: Object as PropType<Loadable<SpotMarket[]>>,
    default: () => ({ loading: false }),
  },
  selectable: {
    type: Boolean,
    default: () => false,
  },
});
const { tab, shipmentSpecId, rateShop, spotMarkets, selectable } =
  toRefs(props);
const emit = defineEmits<{
  (event: 'rate-selected', payload: AcceptQuotePayload): void;
  (event: 'spot-market-cancelled', spotMarketId: number): void;
  (event: 'spot-market-resent', spotMarketId: number): void;
}>();

const { t } = useI18n();
const toast = useToast();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { lowestPriceQuote } = useRateShop(rateShop, {});
const { loadSpotMarkets } = useSpotMarketStore();
const { startSpotMarketPolling } = useSpotMarketPolling();

const lowestCostReasonModal = ref({
  visible: false,
  selectedQuote: new RateQuote(),
  isBrokerage: false,
});

const shouldPromptForRateSelectionReason = computed(() => {
  return (
    isRateSelectionReasonRequired.value &&
    lowestPriceQuote.value &&
    lowestCostReasonModal.value.selectedQuote &&
    lowestCostReasonModal.value.selectedQuote.totalPrice &&
    lowestCostReasonModal.value.selectedQuote.totalPrice >
      (lowestPriceQuote.value.totalPrice ?? 0)
  );
});

const onRateSelected = (rateQuote: RateQuote) => {
  lowestCostReasonModal.value.selectedQuote = rateQuote;
  if (shouldPromptForRateSelectionReason.value) {
    lowestCostReasonModal.value.visible = true;
  } else {
    emit('rate-selected', {
      rateQuote,
      rateSelectionReason: undefined,
    });
  }
};

const onSpotMarketCancelled = async (spotMarketId: number) => {
  const rateShopId = rateShop.value.data?.id;
  if (!rateShopId) {
    return;
  }
  try {
    await api.shipmentSpec.cancelSpotMarket(shipmentSpecId.value, spotMarketId);
    await loadSpotMarkets(rateShopId, {
      bypassCache: true,
      shipmentSpecId: shipmentSpecId.value,
    });
    emit('spot-market-cancelled', spotMarketId);
  } catch (e) {
    toast.addError({
      detail: t('errors.cancelingParam', {
        param: t('carrierPortal.spotMarket.spotMarkets', 1),
      }),
      error: e,
    });
  }
};

const onSpotMarketResent = async (spotMarketId: number) => {
  const rateShopId = rateShop.value.data?.id;
  if (!rateShopId) {
    return;
  }
  try {
    await api.shipmentSpec.resendSpotMarket(shipmentSpecId.value, spotMarketId);
    await loadSpotMarkets(rateShopId, {
      bypassCache: true,
      shipmentSpecId: shipmentSpecId.value,
    });
    startSpotMarketPolling(rateShopId, {
      shipmentSpecId: shipmentSpecId.value,
    });
    emit('spot-market-resent', spotMarketId);
  } catch (e) {
    toast.addError({
      detail: t('errors.resendingParam', {
        param: t('carrierPortal.spotMarket.spotMarkets', 1),
      }),
      error: e,
    });
  }
};

const onRateSelectionConfirmed = (payload: RateSelectionReason) => {
  if (!payload.rateSelectionReasonType) {
    return;
  }

  const rateSelectionReason = new RateSelectionReason(payload);

  emit('rate-selected', {
    rateQuote: lowestCostReasonModal.value.selectedQuote,
    rateSelectionReason,
  });
};
</script>
